import type { IntegrationsExperience } from "types/interfaces/user";

export const CREATE_MODE_DRAFT_KEY = 0;

export const PLATFORM_USER_DRAFTS_STORAGE_KEY = "userManagementPlatformDrafts";

/** Persists which platform user row had the detail drawer open (web user management). */
export const PLATFORM_SELECTED_USER_STORAGE_KEY =
  "userManagementSelectedPlatformId";

export const DRAFT_CLIENT_NAMES_SESSION_KEY = "userManagementDraftClientNames";

export function clearPersistedPlatformUserSelection(): void {
  try {
    window.localStorage.removeItem(PLATFORM_SELECTED_USER_STORAGE_KEY);
  } catch {}
}

function loadDraftClientNamesFromSession(): Record<number, string> {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_CLIENT_NAMES_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const out: Record<number, string> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const id = parseInt(k, 10);
      if (!Number.isNaN(id) && typeof v === "string" && v.trim() !== "") {
        out[id] = v.trim();
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function mergeDraftClientNamesToSession(
  updates: Record<number, string>,
): void {
  try {
    const cur = loadDraftClientNamesFromSession();
    const next = { ...cur, ...updates };
    window.sessionStorage.setItem(
      DRAFT_CLIENT_NAMES_SESSION_KEY,
      JSON.stringify(next),
    );
  } catch {}
}

export interface UserManagementDrafts {
  folders?: Record<number, boolean>;
  /** Maps folderId → selected subfolder names. Empty array = full access (no restriction). */
  folderSubfolders?: Record<number, string[]>;
  integrations?: Record<number, boolean>;
  chatbot?: { chat_id: string; api_key: string };
  /** The access group picked for an internal user; null takes them out of their group. */
  accessGroupId?: number | null;
  /** Roles come from access groups; only the client's Bulk Folder Download is left. */
  permissions?: {
    can_download_top_document_folders?: boolean;
  };
  profile?: {
    roles: {
      is_data_storage_user: boolean;
      is_integration_dashboard_user: boolean;
      /** Legacy dashboard or Integration Platform (CAL-5777); older drafts
       * without the field mean legacy. */
      integrations_experience?: IntegrationsExperience;
      is_ai_agent_user: boolean;
      is_secure_file_upload_user: boolean;
    };
    requiresPasswordChange: boolean;
    /** Set when the admin edited identity fields (drives Unsaved chip / navigation guard). */
    username?: string;
    first_name?: string;
    email?: string;
    client_id?: number | null;
  };
}

export type PlatformUserDraftsMap = Record<number, UserManagementDrafts>;

export interface PlatformDraftSummaryRow {
  userId: number;
  userLabel: string;
  sections: string[];
}

function userDraftHasMeaningfulContent(d: UserManagementDrafts): boolean {
  if (d.profile) return true;
  if (d.chatbot) return true;
  if (d.accessGroupId !== undefined) return true;
  if (d.permissions != null) return true;
  if (d.folders && Object.keys(d.folders).length > 0) return true;
  if (d.folderSubfolders && Object.keys(d.folderSubfolders).length > 0)
    return true;
  if (d.integrations && Object.keys(d.integrations).length > 0) return true;
  return false;
}

export function hasAnyPlatformUserDrafts(
  drafts: PlatformUserDraftsMap,
): boolean {
  for (const d of Object.values(drafts)) {
    if (userDraftHasMeaningfulContent(d)) return true;
  }
  return false;
}

export function loadPlatformUserDrafts(): PlatformUserDraftsMap {
  try {
    const raw = window.localStorage.getItem(PLATFORM_USER_DRAFTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, UserManagementDrafts>;
    const result: PlatformUserDraftsMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      const id = parseInt(k, 10);
      if (!Number.isNaN(id) && v && typeof v === "object") {
        result[id] = v;
      }
    }
    return result;
  } catch {
    return {};
  }
}

export function savePlatformUserDrafts(drafts: PlatformUserDraftsMap): void {
  try {
    window.localStorage.setItem(
      PLATFORM_USER_DRAFTS_STORAGE_KEY,
      JSON.stringify(drafts),
    );
  } catch {}
}

export function clearPlatformUserDrafts(): void {
  try {
    window.localStorage.removeItem(PLATFORM_USER_DRAFTS_STORAGE_KEY);
  } catch {}
}

export function summarizeDraftsForDialog(
  drafts: PlatformUserDraftsMap,
  clientNameByUserId?: Readonly<Partial<Record<number, string>>>,
): PlatformDraftSummaryRow[] {
  const rows: PlatformDraftSummaryRow[] = [];
  const sessionNames = loadDraftClientNamesFromSession();
  const ids = Object.keys(drafts)
    .map((k) => parseInt(k, 10))
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b);

  const resolveClientLabel = (id: number): string => {
    if (id === CREATE_MODE_DRAFT_KEY) return "New user (draft)";
    const fromArg = clientNameByUserId?.[id]?.trim();
    if (fromArg) return fromArg;
    const fromSession = sessionNames[id];
    if (fromSession) return fromSession;
    return `User ID ${id}`;
  };

  for (const id of ids) {
    const d = drafts[id];
    if (!d || !userDraftHasMeaningfulContent(d)) continue;

    const sections: string[] = [];
    if (d.profile) sections.push("Profile");
    if (
      (d.folders && Object.keys(d.folders).length > 0) ||
      (d.folderSubfolders && Object.keys(d.folderSubfolders).length > 0)
    )
      sections.push("Folders");
    if (d.integrations && Object.keys(d.integrations).length > 0)
      sections.push("Integrations");
    if (d.chatbot) sections.push("Chatbot");
    if (d.accessGroupId !== undefined) sections.push("Access group");
    if (d.permissions != null) sections.push("Permissions");

    if (sections.length === 0) continue;

    rows.push({
      userId: id,
      userLabel: resolveClientLabel(id),
      sections,
    });
  }
  return rows;
}
